import { useEffect, useRef } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import styled from 'styled-components'
import noop from 'lodash/noop'
import { Text, Stack, TextLink } from '@machobear/design-system'
import { Button } from '@/components/button'
import CloseButton from '@/components/close-button'
import { Input } from '@/components/form'
import Icon from '@/components/icon'
import { createModal, useModalize } from '@/components/modalize'
import { createFlag } from '@/ports'
import { breakpoints } from '@/styles'
import useProfile from '@/hooks/use-profile'
import { formatPhone } from '@/utils/format-phone'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-height: 90vh;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    max-height: unset;
    height: 100%;
  }
`

const ContentWrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 20px;

  // use after for bottom padding due to Firefox bug:
  // https://stackoverflow.com/questions/29986977
  :after {
    content: '';
    display: block;
    padding-bottom: 45px;
    width: 100%;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    padding: 15px 20px 0;
  }
`

const Header = styled.div`
  height: 65px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 25px 0;
  flex-shrink: 0;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    height: 60px;
    padding: 5px 20px 0;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 410px;
  button {
    width: 50%;
    :last-child {
      margin-left: 20px;
    }
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    flex-direction: column;

    button:last-child {
      margin: 20px 0 0;
    }
  }
`

const ModalHideWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const ModalHideText = styled.p`
  text-align: center;
  margin: 0;
`

const CheckBox = styled(Input)`
  margin-right: 10px;
  flex-shrink: 0;
`

const PublishModal = ({
  isPublished,
  isVerificationRequired,
  history,
  onClose = noop,
  isBoostYourListing
}) => {
  const { t } = useTranslation()
  const { close } = useModalize()
  const profile = useProfile()
  const contentWrapperRef = useRef(null)

  const handleOutsideClick = event => {
    if (contentWrapperRef.current && !contentWrapperRef.current.contains(event.target)) {
      onClose()
    }
  }

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick)

    return () => {
      document.removeEventListener('click', handleOutsideClick)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClose = () => {
    onClose()
    close()
  }

  const handleShareListing = () => {
    history.push('?share=true')
    close()
  }

  function maskPhoneNumber(phoneNumber) {
    const parts = phoneNumber.split('-')
    if (parts.length >= 3) {
      const lastPart = parts.pop() // Extract the last part
      const maskedLastPart = lastPart.slice(0, -3).replace(/\d/g, '*') + lastPart.slice(-3)
      return `${'***'}-${'***'}-${maskedLastPart}`
    }
    return phoneNumber
  }

  const phoneNumber = maskPhoneNumber(formatPhone(profile?.phone_number).replace(/ /g, '-'))

  const title = isPublished
    ? t('l.listing.view.published_listing.text', 'Listing Published!')
    : isVerificationRequired
      ? t('l.verify_listing.view.pending_action_required.text', 'Action required to publish')
      : t('l.verify_listing.view.pending_approval.button', 'Pending Approval')

  const description = isPublished ? (
    <Text size="medium" align="center" tone="secondary">
      {isBoostYourListing
        ? t(
            'l.listing.view.listing_published_boost_message.text',
            'You’re one step away from boosting your listing to the top of the results and find tenants faster.'
          )
        : t(
            'l.listing.view.listing_published_message.text',
            'Here is what you can do next to find your tenant faster.'
          )}
    </Text>
  ) : (
    <Stack space="large">
      <Text tone="secondary" align="center" size="small">
        {isVerificationRequired ? (
          <Trans
            i18nKey="l.listing.view.under_review_verification_message.text"
            values={{
              phoneNumber
            }}
            defaults="Please answer the verification question sent to: <strong>{{phoneNumber}}</strong> for your listing to go live."
          />
        ) : (
          t(
            'l.listing.view.under_review_message.text',
            'Please give us 1-2 business days to review and publish your listing. Only published listings can be sponsored.'
          )
        )}
      </Text>
      <Text align="center" size="small">
        {t('l.listing.view.under_review_message_contact.text', 'Got questions? Contact ')}
        <TextLink href="mailto:support@liv.rent">support@liv.rent</TextLink>.
      </Text>
    </Stack>
  )

  const icon = isPublished ? 'round_check' : 'pending_orange'

  return (
    <Wrapper ref={contentWrapperRef}>
      <Header>
        <CloseButton onClick={handleClose} />
      </Header>
      <ContentWrapper>
        <Stack space="large" align="center">
          <Stack space="large" align="center">
            <Icon id={icon} width={50} height={50} />
            <Stack align="center" space="medium">
              <Text weight="strong" size={isPublished ? 'xlarge' : 'large'}>
                {title}
              </Text>
              {description}
            </Stack>
          </Stack>
          {isPublished && isBoostYourListing && (
            <Button theme="primary" onClick={handleClose}>
              {t('l.listing.view.pending_modal_checkout.text', 'Continue to checkout')}
            </Button>
          )}
          {isPublished && !isBoostYourListing && (
            <ButtonWrapper>
              <Button theme="secondary" onClick={handleShareListing}>
                {t('b.components.success_modal.share.button', 'Share Listing')}
              </Button>
              <Button theme="primary" onClick={handleClose}>
                {t('l.listing.view.view_listing.button', 'View Listing')}
              </Button>
            </ButtonWrapper>
          )}

          {isPublished && !isBoostYourListing && (
            <ModalHideWrapper>
              <CheckBox
                type="checkbox"
                onChange={e =>
                  e.target.checked
                    ? createFlag({
                        body: {
                          key_name: 'skip_listing_published_confirmation',
                          key_value: '1'
                        }
                      })
                    : createFlag({
                        body: {
                          key_name: 'skip_listing_published_confirmation',
                          key_value: '0'
                        }
                      })
                }
              />
              <ModalHideText>
                {t('b.components.success_modal.hide.check_box', 'Do not show this again.')}
              </ModalHideText>
            </ModalHideWrapper>
          )}
        </Stack>
      </ContentWrapper>
    </Wrapper>
  )
}

export const showPublished = createModal(PublishModal, {
  width: 'auto',
  maxWidth: '620px',
  mobileFullscreen: true,
  flexGrow: true,
  border: '1px solid #d8d8d8'
})

export const showPending = createModal(PublishModal, {
  width: 'auto',
  maxWidth: '350px',
  mobileFullscreen: true,
  flexGrow: true,
  border: '1px solid #d8d8d8'
})
